import React, { JSX, useContext, useState } from 'react';
import {
  Field,
  Image,
  ImageField,
  Link,
  LinkField,
  Text,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { classNames } from 'assets/utils/helper';
import Icons from '../atoms/Icons';
import ExternalLinkIcon from '../../assets/images/icons/external-link-icon.svg';
import ArrowCollapse from '../../assets/images/icons/arrow-collapse.svg';
import { StyleguideComponentProps } from 'lib/component-props';
import ThemeContext from 'components/context/ThemeContext';

type DescriptionItem = {
  id: string;
  fields: {
    Title: Field<string>;
    link: LinkField;
  };
};

type SocialLinkItem = {
  id: string;
  name?: string;
  displayName?: string;
  fields: {
    BaseLink: {
      value: {
        href: string;
      };
    };
    BaseSvg: {
      value: string;
    };
  };
};

type LinkItem = {
  id: string;
  fields: {
    Title: Field<string>;
    items: Array<DescriptionItem>;
  };
};

type R2FooterProps = StyleguideComponentProps & {
  fields: {
    Logo: ImageField;
    LogoLink: {
        value: {
          href: string;
        };
      };
    DescriptionLinks: Array<DescriptionItem>;
    FooterDescription: Field<string>;
    SocialLinks: Array<SocialLinkItem>;
    MissionStatement: Field<string>;
    items: Array<LinkItem>;
  };
};

const isExternalLink = (url: string): boolean => {
  return /^(https?:)?\/\/|^www\./i.test(url);
};

const getSocialLinkAriaLabel = (item: SocialLinkItem): string => {
  const label =
    item.displayName ||
    item.name ||
    item.fields?.BaseSvg?.value ||
    item.fields?.BaseLink?.value?.href ||
    'Social media link';

  return `${label} (opens in a new tab)`;
};

const R2Footer = (props: R2FooterProps): JSX.Element => {
  const { fields } = props;
  const [collapsedItems, setCollapsedItems] = useState<{
    [key: string]: boolean;
  }>({});
  const theme = useContext(ThemeContext)?.themeState?.theme;

  const toggleCollapse = (id: string) => {
    setCollapsedItems(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  return (
    <div className="overhauled" id={`r2-footer-${props.rendering?.uid}`}>
      <div className="r2-footer">
        <div className="footer-inner">
          <>
            <div className="footer-container">
              <div className="footer-logo-container">
                <div className="osf-logo">
                  <a href={fields?.LogoLink?.value?.href || '#'} className="logo-link">
                    <Image alt="osf_logo" field={fields?.Logo || ''} />
                  </a>
                </div>

                <div className="mission-text">
                  <em>{fields?.MissionStatement?.value}</em>
                </div>
              </div>
              <div className="social-links">
                {fields?.SocialLinks?.map(item => (
                  <a
                    key={item.id}
                    href={item?.fields?.BaseLink?.value?.href || '#'}
                    rel="noreferrer"
                    target="_blank"
                    className="social-link"
                    aria-label={getSocialLinkAriaLabel(item)}
                  >
                    <span
                      className={classNames(
                        'icon',
                        `icon-${item?.fields?.BaseSvg?.value}` || ''
                      )}
                      aria-hidden="true"
                    >
                      <Icons
                        id={`${item?.fields?.BaseSvg?.value}-round` || ''}
                      />
                    </span>
                  </a>
                ))}
              </div>
              <div className="link-blocks-container">
                {fields?.items?.map(({ id, fields }) => {
                  const isCollapsed = collapsedItems[id];

                  return (
                    <div key={id} className="footer-link-block">
                      <div className="footer-link-header">
                        <Text
                          className="footer-link-title osf-nav-section-titles"
                          tag="h5"
                          field={fields?.Title || ''}
                          onClick={() => toggleCollapse(id)}
                        />
                        <ArrowCollapse
                          className={`arrow-collapse ${isCollapsed ? 'rotated' : ''}`}
                          onClick={() => toggleCollapse(id)}
                        />
                      </div>
                      <ul
                        className={`footer-link-list ${collapsedItems[id] ? 'expanded' : ''}`}
                      >
                        {fields?.items?.map(({ id, fields }) => (
                          <li key={id}>
                            {fields?.link.value?.href && (
                              <Link
                                className="osf-nav-body-copy footer-link"
                                aria-label="Link Learn More"
                                rel="noreferrer"
                                field={fields?.link}
                                onClick={e => e?.stopPropagation()}
                              >
                                <span className="link-content">
                                  {fields?.link.value.text}
                                  {isExternalLink(fields.link.value.href) && (
                                    <span className="externalLink-icon">
                                      <ExternalLinkIcon />
                                    </span>
                                  )}
                                </span>
                              </Link>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="footer-legal">
              <Text
                className="copyright-text"
                field={fields?.FooterDescription || ''}
              />
              <div className="legal-links">
                {fields?.DescriptionLinks?.map(({ id, fields }) => (
                  <div className="legal-link" key={id}>
                    {fields?.link?.value?.href && (
                      <Link
                        aria-label="Link Learn More"
                        rel="noreferrer"
                        field={fields?.link}
                        onClick={e => e?.stopPropagation()}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        </div>
        <div
          className={classNames(
            'overhauled footer-border',
            theme === 'sacon' || theme === 'sfcon'
              ? 'bg-primary-dark'
              : 'gradient-horizontal'
          )}
        ></div>
      </div>
    </div>
  );
};

export default R2Footer;
